import { NumericStepper, Text } from "flicket-ui";
import { IReleaseTicket } from "~features/nonseated-reservation/nonseated-reservation.types";
import { TicketTypeOnSaleStatus } from "~graphql/sdk";
import { TicketAvailabilityStatus } from "~graphql/typed-document-nodes";
import { useAtom } from "jotai";
import { isPrechargeFlowAtom } from "~features/nonseated-reservation/isPrechargeFlowAtom";

interface TicketSelectionControlProps {
  ticket: IReleaseTicket;
  quantity: number;
  onChange: (value: number) => void;
  variant: "inline" | "modal";
  allowZero?: boolean;
  disableDecrement?: boolean;
}

export const TicketSelectionControl = ({
  ticket,
  quantity,
  onChange,
  variant,
  allowZero = false,
  disableDecrement = false,
}: TicketSelectionControlProps) => {
  const isDisabled =
    ticket.onSaleStatus !== TicketTypeOnSaleStatus.OnSale ||
    ticket.availabilityStatus !== TicketAvailabilityStatus.Available;

  const [isPrechargeFlow] = useAtom(isPrechargeFlowAtom);

  const getButtonLabel = () => {
    if (ticket.onSaleStatus === TicketTypeOnSaleStatus.OnSaleEnded) {
      return ticket.notOnSaleMessage;
    }

    if (
      ticket.availabilityStatus === TicketAvailabilityStatus.AllocationExhausted
    ) {
      return "Unavailable";
    }

    if (
      ticket.availabilityStatus === TicketAvailabilityStatus.Soldout ||
      ticket.isSoldOut
    ) {
      return "Sold Out";
    }

    return "Select";
  };

  // Not yet on sale
  if (ticket.onSaleStatus === TicketTypeOnSaleStatus.NotYetOnSale) {
    if (variant === "modal") {
      return (
        <Text variant="regular">Sale starts {ticket.notOnSaleMessage}</Text>
      );
    }
    return (
      <>
        <Text variant="regular" textAlign={"end"}>
          Sale starts
        </Text>
        <Text variant="regular">{ticket.notOnSaleMessage}</Text>
      </>
    );
  }

  if (
    ticket.onSaleStatus === TicketTypeOnSaleStatus.OnSale &&
    (ticket.availabilityStatus === TicketAvailabilityStatus.Soldout ||
      ticket.isSoldOut) &&
    isPrechargeFlow
  ) {
    return (
      <Text variant="header.XS" color="N500">
        Early access exhausted
      </Text>
    );
  }

  return (
    <NumericStepper
      value={quantity}
      onChange={onChange}
      min={ticket.minPurchaseQuantity}
      max={ticket.maxPurchaseQuantity}
      allowZero={allowZero}
      disableDecrement={disableDecrement}
      buttonLabel={getButtonLabel()}
      disabled={isDisabled}
    />
  );
};
